import React from 'react';
import {Control, UseFormGetValues, UseFormRegister, UseFormSetValue} from 'react-hook-form';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import FormGroup from 'shared/ui/FormGroup/FormGroup';
import PremiumCheckbox from 'shared/ui/Checkbox/PremiumCheckbox/PremiumCheckbox';
import IHttpMonitorOutput from 'features/http/model/HttpMonitor/IHttpMonitor';
import {useCheckboxBlock} from 'features/http/model/useCheckboxBlock';
import {isTranslate} from 'shared/lib/lang';

import {getContentCheckHint} from '../CheckingContent/CheckingContent';
import BasicAuthField from './BasicAuthField/BasicAuthField';
import ContentField from './ContentField/ContentField';
import FieldCard from './FieldCard/FieldCard';
import ParseErrorsField from './ParseErrorsField/ParseErrorsField';
import ScreenshotField from './ScreenshotField/ScreenshotField';
import {getParseErrorsHelpText, getScreenshotOnErrorHint} from './checkboxBlockHints';

import styles from './CheckboxBlock.module.scss';

interface ICheckboxBlockProps {
    item?: IHttpMonitorOutput,
    getValues: UseFormGetValues<any>,
    control: Control<any>,
    register: UseFormRegister<any>,
    setValue: UseFormSetValue<any>,
    activeKeys?: string[],
    onRemoveField?: (_key: string) => void,
}

const CheckboxBlock: React.FC<ICheckboxBlockProps> = ({
    item,
    getValues,
    control,
    register,
    setValue,
    activeKeys,
    onRemoveField,
}) => {
    const {
        isTariffEnabled,
        freeTariff,
        selective,
        isActive,
        enabledBasicAuth,
        isParseErrorsChecked,
        isContentMonitoringChecked,
        isScreenshotChecked,
        isParseErrorsJson,
        bodyMethodDisabled,
        screenshotActive,
        checkFeatures,
        handleBodyFieldClick,
    } = useCheckboxBlock({control, getValues, setValue, activeKeys});

    const removeHandler = (key: string) => (onRemoveField ? () => onRemoveField(key) : undefined);

    const comingSoonFeatures = [
        {label: t`Проверка подсистем`, help: getParseErrorsHelpText()},
        {label: t`Мониторинг контента`, help: getContentCheckHint()},
        {label: t`Скриншот страницы ошибки`, help: getScreenshotOnErrorHint()},
    ];

    return (
        <div className={cn(styles.container, freeTariff && styles.freeContainer)}>
            {checkFeatures
                .filter(feature => isActive(feature.name))
                .map(feature => (
                    <FieldCard
                        key={feature.name}
                        selective={selective}
                        freeTariff={freeTariff}
                        onRemove={removeHandler(feature.name)}
                    >
                        <PremiumCheckbox
                            label={feature.label}
                            helpText={feature.help}
                            register={register(feature.name)}
                            enabled={isTariffEnabled(feature.condition)}
                            comingSoon={isTranslate() && feature.name === 'frequent_checking'}
                        />
                    </FieldCard>
                ))}

            {isActive('auth_enabled') && (
                <BasicAuthField
                    item={item}
                    control={control}
                    register={register}
                    selective={selective}
                    freeTariff={freeTariff}
                    enabled={isTariffEnabled('monitor_http_basic_auth_enabled')}
                    isChecked={enabledBasicAuth}
                    onRemove={removeHandler('auth_enabled')}
                />
            )}

            {!selective && isTranslate() && comingSoonFeatures.map(({label, help}) => (
                <FormGroup
                    key={label}
                    className={cn(styles.formGroup, freeTariff && styles.freeformGroup)}
                >
                    <PremiumCheckbox
                        label={label}
                        helpText={help}
                        enabled={false}
                        comingSoon
                    />
                </FormGroup>
            ))}

            {!selective && !isTranslate() && freeTariff && (
                <>
                    <PremiumCheckbox
                        label={t`Проверка подсистем`}
                        helpText={getParseErrorsHelpText()}
                        register={register('parse_errors')}
                        enabled={isTariffEnabled('monitor_parse_errors')}
                    />
                    <PremiumCheckbox
                        label={t`Мониторинг контента`}
                        helpText={getContentCheckHint()}
                        register={register('monitor_content_enabled')}
                        enabled={isTariffEnabled('monitor_content_enabled')}
                    />
                </>
            )}

            {!isTranslate() && isTariffEnabled('monitor_parse_errors') && isActive('parse_errors') && (
                <ParseErrorsField
                    item={item}
                    control={control}
                    register={register}
                    selective={selective}
                    freeTariff={freeTariff}
                    isChecked={isParseErrorsChecked}
                    isJson={isParseErrorsJson}
                    bodyMethodDisabled={bodyMethodDisabled}
                    onCheckboxClick={handleBodyFieldClick(isParseErrorsChecked)}
                    onRemove={removeHandler('parse_errors')}
                />
            )}

            {!isTranslate() && screenshotActive && (
                <ScreenshotField
                    register={register}
                    selective={selective}
                    freeTariff={freeTariff}
                    enabled={isTariffEnabled('monitor_screenshot_on_error')}
                    bodyMethodDisabled={bodyMethodDisabled}
                    onCheckboxClick={handleBodyFieldClick(isScreenshotChecked)}
                    onRemove={removeHandler('screenshot_on_error')}
                />
            )}

            {!isTranslate() && isTariffEnabled('monitor_content_enabled') && isActive('monitor_content_enabled') && (
                <ContentField
                    item={item}
                    getValues={getValues}
                    control={control}
                    register={register}
                    selective={selective}
                    freeTariff={freeTariff}
                    bodyMethodDisabled={bodyMethodDisabled}
                    onCheckboxClick={handleBodyFieldClick(isContentMonitoringChecked)}
                    onRemove={removeHandler('monitor_content_enabled')}
                />
            )}
        </div>
    );
};

export default React.memo(CheckboxBlock);
